import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEvent, useEventUsers, useUserByQrCode, useCreateUser } from '@/lib/api/queries'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, User, DollarSign, ArrowRightLeft, Eye, EyeOff, QrCode, Plus, UserPlus, X } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import { useModal } from '@/components/modals/modal-container'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { cn } from '@/lib/utils'
import { QRScannerModal } from '@/components/qr/qr-scanner-modal'

export const Route = createFileRoute('/_authenticated/events/$eventId/support')({
  component: SupportPage,
})

function SupportPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const navigate = useNavigate()
  const { openUserModal } = useModal()
  
  // Permission checks
  const canViewSupport = usePermission('can_view_support')
  const canCreateUser = usePermission('can_create_user')
  const canEditUsers = usePermission('can_edit_users')
  void canEditUsers // Future use

  const [showCreateUserModal, setShowCreateUserModal] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const [selectedUser, setSelectedUser] = useState<number | null>(null)
  const [showScanner, setShowScanner] = useState(false)
  const [qrError, setQrError] = useState('')
  const qrLookup = useUserByQrCode()
  
  // All hooks must be called before any early returns
  const { data: event } = useEvent(eventIdNum)
  // Only fetch users when there's a search term
  const { data: usersData, isLoading } = useEventUsers(eventIdNum, {
    search: debouncedSearch || undefined,
    page_size: 10,
  }, { enabled: !!debouncedSearch })
  
  // Permission guard - now safe after all hooks
  if (!canViewSupport) {
    return <AccessDenied message="You don't have permission to access support functions." />
  }

  const users = usersData?.results || []
  const selectedUserData = users.find(u => u.id === selectedUser)

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value)
  }

  const handleQrScan = async (code: string) => {
    if (!code.trim()) return
    try {
      setQrError('')
      const user = await qrLookup.mutateAsync({ qrCode: code.trim() })
      setShowScanner(false)
      openUserModal(user.id, eventIdNum)
    } catch (err: any) {
      setQrError(err?.message || 'No user found for that QR code')
    }
  }

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
          Support
        </h1>
        {event && (
          <p className="text-neutral-600 dark:text-neutral-400 mt-1">
            {event.name}
          </p>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 sm:grid-cols-2 mb-6">
        <Link to="/events/$eventId/reload" params={{ eventId }} search={{ userId: undefined }}>
          <Card className="hover:border-blue-300 dark:hover:border-blue-700 transition-colors cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 dark:text-white">
                  Add Tokens
                </h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Reload a user's account
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/events/$eventId/transfer" params={{ eventId }} search={{ fromUserId: undefined, toUserId: undefined }}>
          <Card className="hover:border-blue-300 dark:hover:border-blue-700 transition-colors cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                <ArrowRightLeft className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 dark:text-white">
                  Transfer Tokens
                </h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Move tokens between accounts
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* User Search */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-neutral-900 dark:text-white">Find User</CardTitle>
          {canCreateUser && (
            <Button
              onClick={() => setShowCreateUserModal(true)}
              className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-sm text-xs font-medium"
            >
              <Plus className="w-3 h-3 mr-1" />
              New User
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                type="search"
                placeholder="Search by name, email, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowScanner(true)}
              className="shrink-0"
              title="Scan QR Code"
            >
              <QrCode className="w-4 h-4 mr-2" />
              Scan
            </Button>
          </div>

          {qrError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-200 text-sm">
              {qrError}
            </div>
          )}

          {isLoading || (search && search !== debouncedSearch) ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />
            </div>
          ) : debouncedSearch && users.length === 0 ? (
            <div className="py-8 text-center text-neutral-500 dark:text-neutral-400">
              No users found matching "{debouncedSearch}"
            </div>
          ) : debouncedSearch && users.length > 0 ? (
            <div className="space-y-2">
              {users.map((user) => (
                <button
                  key={user.id}
                  onClick={() => setSelectedUser(selectedUser === user.id ? null : user.id)}
                  className={cn(
                    "w-full flex items-center justify-between p-4 rounded-lg border transition-colors text-left",
                    selectedUser === user.id
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                      : "border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900/50"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="font-medium text-neutral-900 dark:text-white">
                        {user.first_name} {user.last_name}
                      </p>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        {user.phone_number || user.email}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-neutral-900 dark:text-white">
                      {event?.event_type === 'token' 
                        ? `${typeof user.balance === 'object' ? user.balance?.balance || 0 : user.balance || 0} Tokens`
                        : formatCurrency(typeof user.balance === 'object' ? user.balance?.balance || 0 : user.balance || 0)
                      }
                    </p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">
                      Balance
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-neutral-500 dark:text-neutral-400">
              Enter a search term to find users
            </div>
          )}
        </CardContent>
      </Card>

      <QRScannerModal
        isOpen={showScanner}
        onScan={handleQrScan}
        onClose={() => setShowScanner(false)}
        title="Scan User QR Code"
      />

      {/* Floating Action Buttons - shown when user is selected */}
      {selectedUser && selectedUserData && (
        <div className="sticky bottom-6 z-50 flex justify-center mt-6">
          <div className="flex items-center gap-3 bg-white dark:bg-neutral-900 rounded-full shadow-lg border border-neutral-200 dark:border-neutral-700 px-2 py-2">
            <div className="pl-4 pr-2 border-r border-neutral-200 dark:border-neutral-700">
              <p className="text-sm font-medium text-neutral-900 dark:text-white">
                {selectedUserData.first_name} {selectedUserData.last_name}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openUserModal(selectedUser, eventIdNum)}
              className="rounded-full gap-2"
            >
              <Eye className="w-4 h-4" />
              View User
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate({ 
                to: '/events/$eventId/reload', 
                params: { eventId },
                search: { userId: selectedUser }
              })}
              className="rounded-full gap-2"
            >
              <DollarSign className="w-4 h-4" />
              Add Tokens
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate({ 
                to: '/events/$eventId/transfer', 
                params: { eventId },
                search: { fromUserId: selectedUser, toUserId: undefined }
              })}
              className="rounded-full gap-2"
            >
              <ArrowRightLeft className="w-4 h-4" />
              Transfer
            </Button>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <CreateUserModal
          eventId={eventIdNum}
          onClose={() => setShowCreateUserModal(false)}
        />
      )}
    </div>
  )
}

// Country code options for phone number
const COUNTRY_CODES = [
  { code: '+1', label: '+1 (US/CA)' },
  { code: '+44', label: '+44 (UK)' },
  { code: '+33', label: '+33 (FR)' },
  { code: '+49', label: '+49 (DE)' },
  { code: '+81', label: '+81 (JP)' },
  { code: '+86', label: '+86 (CN)' },
  { code: '+91', label: '+91 (IN)' },
  { code: '+61', label: '+61 (AU)' },
  { code: '+55', label: '+55 (BR)' },
  { code: '+52', label: '+52 (MX)' },
  { code: '+7', label: '+7 (RU)' },
  { code: '+39', label: '+39 (IT)' },
  { code: '+34', label: '+34 (ES)' },
  { code: '+31', label: '+31 (NL)' },
  { code: '+46', label: '+46 (SE)' },
  { code: '+47', label: '+47 (NO)' },
  { code: '+45', label: '+45 (DK)' },
  { code: '+41', label: '+41 (CH)' },
  { code: '+43', label: '+43 (AT)' },
  { code: '+32', label: '+32 (BE)' },
]

function CreateUserModal({ eventId, onClose }: { eventId: number; onClose: () => void }) {
  const createUser = useCreateUser()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [countryCode, setCountryCode] = useState('+1')
  const [phone, setPhone] = useState('')
  const [passwordMode, setPasswordMode] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    document.body.classList.add('overflow-hidden')
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.classList.remove('overflow-hidden')
    }
  }, [onClose])

  const validate = (): boolean => {
    const errors: Record<string, string> = {}

    if (!firstName.trim()) errors.firstName = 'First name is required'
    if (!lastName.trim()) errors.lastName = 'Last name is required'

    if (passwordMode) {
      if (!password) {
        errors.password = 'Password is required'
      } else if (password.length < 8) {
        errors.password = 'Password must be at least 8 characters'
      }
      if (password !== confirmPassword) {
        errors.confirmPassword = 'Passwords do not match'
      }
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    setError(null)
    if (!validate()) return

    const payload: {
      event: number
      first_name: string
      last_name: string
      email?: string
      phone_number?: string
      password?: string
      confirm_password?: string
    } = {
      event: eventId,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
    }

    if (email.trim()) payload.email = email.trim()
    if (phone.trim()) payload.phone_number = `${countryCode}${phone.trim()}`
    if (passwordMode && password) {
      payload.password = password
      payload.confirm_password = confirmPassword
    }

    try {
      await createUser.mutateAsync(payload)
      onClose()
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'message' in err) {
        setError((err as Error).message)
      } else {
        setError('Failed to create user')
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative bg-white dark:bg-neutral-900 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <UserPlus className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">New User</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          {/* First Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={firstName}
              onChange={(e) => { setFirstName(e.target.value); setFieldErrors(prev => ({ ...prev, firstName: '' })) }}
              placeholder="Enter first name"
              className={cn(fieldErrors.firstName && 'border-red-500')}
            />
            {fieldErrors.firstName && <p className="text-xs text-red-500 mt-1">{fieldErrors.firstName}</p>}
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={lastName}
              onChange={(e) => { setLastName(e.target.value); setFieldErrors(prev => ({ ...prev, lastName: '' })) }}
              placeholder="Enter last name"
              className={cn(fieldErrors.lastName && 'border-red-500')}
            />
            {fieldErrors.lastName && <p className="text-xs text-red-500 mt-1">{fieldErrors.lastName}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Email
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email (optional)"
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Phone Number
            </label>
            <div className="flex gap-2">
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="w-32 px-2 py-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-sm text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {COUNTRY_CODES.map((cc) => (
                  <option key={cc.code} value={cc.code}>{cc.label}</option>
                ))}
              </select>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number (optional)"
                className="flex-1"
              />
            </div>
          </div>

          {/* Password Mode Toggle */}
          <div className="border-t border-neutral-200 dark:border-neutral-800 pt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Set Password Directly
              </label>
              <button
                type="button"
                onClick={() => {
                  setPasswordMode(!passwordMode)
                  setPassword('')
                  setConfirmPassword('')
                  setFieldErrors(prev => ({ ...prev, password: '', confirmPassword: '' }))
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  passwordMode ? 'bg-blue-600' : 'bg-neutral-300 dark:bg-neutral-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    passwordMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            {!passwordMode && (
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                A temporary password will be emailed to the user if an email is provided.
              </p>
            )}
          </div>

          {/* Password Fields */}
          {passwordMode && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setFieldErrors(prev => ({ ...prev, password: '' })) }}
                    placeholder="Min. 8 characters"
                    className={cn('pr-10', fieldErrors.password && 'border-red-500')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400 hover:text-neutral-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {fieldErrors.password && <p className="text-xs text-red-500 mt-1">{fieldErrors.password}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setFieldErrors(prev => ({ ...prev, confirmPassword: '' })) }}
                  placeholder="Confirm password"
                  className={cn(fieldErrors.confirmPassword && 'border-red-500')}
                />
                {fieldErrors.confirmPassword && <p className="text-xs text-red-500 mt-1">{fieldErrors.confirmPassword}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t border-neutral-200 dark:border-neutral-800">
          <Button variant="outline" onClick={onClose} disabled={createUser.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createUser.isPending}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {createUser.isPending ? 'Creating...' : 'Create User'}
          </Button>
        </div>
      </div>
    </div>
  )
}
